import type { StatusKind } from "@/shared/ui/StatusDot";

/**
 * health — 노드·사이트 신선도 판정과 표시의 단일 정본.
 *
 * 정본 문서: docs/노드_신선도_상태_규격_v1.md (백엔드·관제콘솔과 공유).
 * 임계값·health 값·색·문구를 여기 말고 다른 곳에서 정의하지 말 것.
 *
 * 이 파일이 생긴 이유: `SensorNode.status` 는 ingest 가 online 으로 쓰기만 하는
 * 단방향 래치였고, 화면 여덟 곳이 그 문자열만 세었다. 센서가 한 대도 안 켜진
 * 현장이 「정상」으로 보였다는 뜻이다. 가스 관제에서 죽은 센서와 「가스 정상」이
 * 화면에서 구별되지 않는 것은 안전 결함이므로, 판정은 마지막 수신 시각으로만
 * 하고 status 문자열은 fault 자가진단 용도로만 읽는다.
 */

/** 규격 §1 — 임계값 정본. 데이터 간격 5초 기준 30주기. */
export const STALE_SEC = 150;
/** 규격 §1 — 통신두절 판정. */
export const OFFLINE_SEC = 600;

/** 규격 §2 — 노드 health 6값. */
export type NodeHealth = "online" | "warming" | "stale" | "offline" | "fault" | "unknown";
/** 규격 §3 — 사이트 health 5값. */
export type SiteHealth = "ok" | "degraded" | "no_data" | "not_installed" | "no_device";
/** 규격 §5 — 데모/시뮬 구분. */
export type SiteKind = "live" | "demo" | "sim";

/** Badge 의 tone 유니온과 같은 값. Badge 를 import 하면 lib→ui 순환이 생긴다. */
export type HealthBadgeTone = "neutral" | "info" | "success" | "caution" | "warning" | "danger";

/** 규격 §6 표시 규칙 한 줄. 색은 토큰 이름으로 들고 있어야 색각 팔레트가 따라온다. */
export interface HealthTone {
  /** StatusDot 의 색+모양 키. */
  kind: StatusKind;
  /** Badge tone. */
  badge: HealthBadgeTone;
  /** i18n 문구 키 (§6 의 표시 문구). */
  labelKey: string;
  /** 선·아이콘 토큰. */
  tone: string;
  /** `tone` 위가 아닌 일반 배경에 올리는 텍스트 토큰. */
  fg: string;
  /**
   * 회색(판단 불가) 여부. 「데이터 없음」은 「정상」이 아니다 — 이 플래그가 true 인
   * 상태를 초록 계열로 접거나 정상 집계에 넣으면 규격 위반이다.
   */
  muted: boolean;
}

const NODE_TONE: Record<NodeHealth, HealthTone> = {
  online:  { kind: "ok",       badge: "success", labelKey: "common.health.node.online",  tone: "--success",     fg: "--success-fg", muted: false },
  warming: { kind: "info",     badge: "info",    labelKey: "common.health.node.warming", tone: "--accent",      fg: "--accent",     muted: false },
  stale:   { kind: "caution",  badge: "caution", labelKey: "common.health.node.stale",   tone: "--caution",     fg: "--caution-fg", muted: false },
  offline: { kind: "critical", badge: "danger",  labelKey: "common.health.node.offline", tone: "--danger",      fg: "--danger-fg",  muted: false },
  fault:   { kind: "critical", badge: "danger",  labelKey: "common.health.node.fault",   tone: "--danger",      fg: "--danger-fg",  muted: false },
  unknown: { kind: "offline",  badge: "neutral", labelKey: "common.health.node.unknown", tone: "--fg-disabled", fg: "--fg-subtle",  muted: true  },
};

const SITE_TONE: Record<SiteHealth, HealthTone> = {
  ok:            { kind: "ok",       badge: "success", labelKey: "common.health.site.ok",            tone: "--success",     fg: "--success-fg", muted: false },
  degraded:      { kind: "caution",  badge: "caution", labelKey: "common.health.site.degraded",      tone: "--caution",     fg: "--caution-fg", muted: false },
  no_data:       { kind: "critical", badge: "danger",  labelKey: "common.health.site.no_data",       tone: "--danger",      fg: "--danger-fg",  muted: false },
  not_installed: { kind: "offline",  badge: "neutral", labelKey: "common.health.site.not_installed", tone: "--fg-disabled", fg: "--fg-subtle",  muted: true  },
  no_device:     { kind: "offline",  badge: "neutral", labelKey: "common.health.site.no_device",     tone: "--fg-disabled", fg: "--fg-subtle",  muted: true  },
};

export const nodeTone = (h: NodeHealth): HealthTone => NODE_TONE[h] ?? NODE_TONE.unknown;
export const siteTone = (h: SiteHealth): HealthTone => SITE_TONE[h] ?? SITE_TONE.no_device;

/** 노드가 살아 있다고 셀 수 있는 상태 (§3 `nodes_fresh`). */
export const isFresh = (h: NodeHealth): boolean => h === "online" || h === "warming";

/** 판정에 필요한 최소 필드만. SensorNode 를 import 하면 models→lib 순환이 생긴다. */
export interface NodeLike {
  status?: string | null;
  last_seen_at?: string | null;
  /** 서버가 §4 대로 내려주는 파생값. 있으면 그대로 쓴다. */
  health?: string | null;
}

const NODE_HEALTHS: readonly string[] = ["online", "warming", "stale", "offline", "fault", "unknown"];

/**
 * 규격 §2 판정. 서버가 `health` 를 내려주면 그것이 정본이고, 없을 때만(구버전
 * 응답·WS 로 조립한 임시 객체) 마지막 수신 시각으로 같은 식을 다시 푼다.
 *
 * `last_seen_at` NULL 검사가 age 계산보다 먼저다 — unknown(설치 안 됨)과
 * offline(붙었다가 끊김)을 합치면 「설치 전」이 「장비 고장」으로 읽힌다.
 */
export function nodeHealth(node: NodeLike, nowMs: number = Date.now()): NodeHealth {
  if (node.health && NODE_HEALTHS.includes(node.health)) return node.health as NodeHealth;
  if (node.status === "fault") return "fault";
  if (!node.last_seen_at) return "unknown";
  const seen = Date.parse(node.last_seen_at);
  if (!Number.isFinite(seen)) return "unknown";
  const age = (nowMs - seen) / 1000;
  if (age > OFFLINE_SEC) return "offline";
  if (node.status === "warming") return "warming";
  if (age > STALE_SEC) return "stale";
  return "online";
}

/**
 * 판독 시각 하나의 신선도. 카드의 「n초 전」 라벨처럼 노드가 아니라 값의 나이를
 * 물을 때 쓴다. `atMs == null` 은 측정이 한 건도 없다는 뜻이므로 unknown(회색)이다
 * — 예전에는 이 경우 stale=false 로 접혀서, 측정 0건이 61초 지연보다 덜 경고됐다.
 */
export function readingHealth(atMs: number | null | undefined, nowMs: number): NodeHealth {
  if (atMs == null) return "unknown";
  const age = (nowMs - atMs) / 1000;
  if (age > OFFLINE_SEC) return "offline";
  if (age > STALE_SEC) return "stale";
  return "online";
}

/**
 * 가스 **채널 하나**의 신선도 한도(초). 백엔드 `RISK_MAX_DATA_AGE_SECONDS`(기본 180)와
 * 같은 값이다 — 위험도 채점이 이보다 묵은 값을 이미 쓰지 않으므로, 화면도 그 값을
 * 「현재 값」으로 그리지 않는다.
 *
 * ⛔ 노드 신선도(STALE_SEC)와 **다른 축**이다. 노드가 다른 가스를 계속 보내는 동안 한
 *    채널(예: 연기 모듈 하나)만 끊기면 노드 판정은 「정상」이고 `latest` 는 그 채널의
 *    24시간 내 마지막 값을 계속 내려 준다 — 멈춘 값이 현재 값처럼 보였다(2026-09-28).
 */
export const GAS_STALE_SEC = 180;

/** 채널 판정에 필요한 `latest` 행 필드만. hooks/readings 를 import 하면 api→lib 순환이 생긴다. */
export interface ChannelReadingLike {
  time: string;
  measured_at?: string | null | undefined;
  age_s?: number | null | undefined;
  stale?: boolean | null | undefined;
  fetched_at_ms?: number | null | undefined;
}

/**
 * `latest` 한 행을 「현재 값」으로 그려도 되는가 — 아니면 true.
 *
 * 서버 판정이 정본이다 — `stale` 이 참이면 끝, 아니면 `age_s` 에 **받아 온 뒤 흐른 시간**(`fetched_at_ms`)을
 * 더해 다시 푼다(폴링이 멈춘 채 남은 행). 둘 다 없을 때만(구버전 응답) 잰 시각
 * (모르면 도착 시각)으로 같은 한도를 다시 푼다. 시각을 못 읽는 행은 판정하지 않는다(false).
 */
export function isChannelStale(row: ChannelReadingLike, nowMs: number = Date.now()): boolean {
  if (row.stale === true) return true;
  if (typeof row.age_s === "number") {
    // 서버 판정은 응답 순간 기준 — 폴링이 멈춘 채 남은 행이 영원히 「현재 값」이 되지 않게 받아 온 뒤 흐른 시간을 더한다.
    const since =
      typeof row.fetched_at_ms === "number" && Number.isFinite(row.fetched_at_ms)
        ? Math.max(0, (nowMs - row.fetched_at_ms) / 1000)
        : 0;
    return row.age_s + since > GAS_STALE_SEC;
  }
  if (row.stale === false) return false;
  const at = Date.parse(row.measured_at ?? row.time);
  if (!Number.isFinite(at)) return false;
  return (nowMs - at) / 1000 > GAS_STALE_SEC;
}

/**
 * WebSocket 으로 받은 채널 값의 신선도 — 같은 한도. `atMs` 는 그 채널을 마지막으로
 * **받은** 시각(브라우저 시계)이라 시계 차가 없다. 한 번도 못 받았으면(null) stale.
 */
export function isPushStale(atMs: number | null | undefined, nowMs: number): boolean {
  return atMs == null || (nowMs - atMs) / 1000 > GAS_STALE_SEC;
}

/** 규격 §3 — 노드 health 목록 → 사이트 health. */
export function siteHealthFrom(healths: readonly NodeHealth[]): SiteHealth {
  if (healths.length === 0) return "no_device";
  if (healths.every((h) => h === "unknown")) return "not_installed";
  const fresh = healths.filter(isFresh).length;
  if (fresh === 0) return "no_data";
  if (fresh < healths.length) return "degraded";
  return "ok";
}

/** 서버가 §4 대로 내려주는 사이트 집계. */
export interface SiteLike {
  health?: string | null;
  nodes_total?: number | null;
  nodes_fresh?: number | null;
  last_reading_at?: string | null;
  kind?: string | null;
}

const SITE_HEALTHS: readonly string[] = ["ok", "degraded", "no_data", "not_installed", "no_device"];

/**
 * 사이트 health 를 얻는 유일한 경로. 서버 집계 → 손에 든 노드 목록 → 집계 카운트
 * 순으로 내려가고, 셋 다 없으면 no_device(회색)로 끝낸다. 모르는 것을 ok 로 접지
 * 않기 위한 기본값이다.
 */
export function siteHealthOf(site: SiteLike, nodes?: readonly NodeLike[], nowMs?: number): SiteHealth {
  if (site.health && SITE_HEALTHS.includes(site.health)) return site.health as SiteHealth;
  if (nodes) return siteHealthFrom(nodes.map((n) => nodeHealth(n, nowMs)));
  const total = site.nodes_total ?? null;
  const fresh = site.nodes_fresh ?? 0;
  if (total == null) return "no_device";
  if (total === 0) return "no_device";
  // 서버 집계만 있을 때는 노드별 unknown 을 못 보므로, 수신 이력 자체가 없는
  // 사이트를 「설치 전」으로 되살린다.
  if (fresh === 0) return site.last_reading_at ? "no_data" : "not_installed";
  if (fresh < total) return "degraded";
  return "ok";
}

/** 규격 §5 — 관제 기본 스코프가 아닌 사이트(데모·시뮬)면 배지 키, 아니면 null. */
export function siteKindBadgeKey(site: SiteLike): string | null {
  if (site.kind === "demo") return "common.health.kind.demo";
  if (site.kind === "sim") return "common.health.kind.sim";
  return null;
}
